/**
 * 何段かの見出し。列ごとに、上の段から下の段への見出しの並び（path）を持ち、続く列の同じ見出しを1つのセルにまとめる。
 * 一番下の見出しは、そこから最下段までの高さを使い、名前と単位を2行で示す。押すと並べ替える。
 * 見出しの行はスクロールしても動かないよう、段ごとの高さを決めて、段ごとに上からの位置をずらす。
 */

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  TableCell,
  TableRow,
  TableSortLabel,
  Tooltip,
  type SxProps,
  type Theme,
} from '@mui/material';
import type { ReactNode } from 'react';

import type { SortState } from './sorting';

/** 見出しの段の高さ。最下段は名前と単位の2行 */
const GROUP_ROW_HEIGHT = 28;
const LEAF_ROW_HEIGHT = 48;

/**
 * 見出しのセル。段の高さに収まるよう余白を詰め、消費項目の名前（Athena など）を大文字にしない。
 * テーマ（MuiTableHead）と表の余白（compactTableSx）の指定より優先させるため、クラスを重ねて詳細度を上げる
 */
const headCellSx = { '&&&': { py: 0.5, lineHeight: 1.4, textTransform: 'none' } } as const;

export interface HeaderColumn {
  key: string;
  /** 上の段から下の段への見出し。最後が列の名前 */
  path: string[];
  /** path の段ごとの、見出しに添える説明。まとめたセルでは、その先頭の列のものを使う */
  pathHints?: (string | undefined)[];
  unit?: string;
  numeric?: boolean;
  /** 並べ替えられる列 */
  sortable?: boolean;
  /** 見出しのセルの中身を差し替える（お気に入りの星など） */
  content?: ReactNode;
  /** 見出しの名前の前に置く部品（下の行をまとめて開け閉めする印など） */
  leading?: ReactNode;
  /** 横スクロールしても動かない列のセルの設定 */
  cell?: { className?: string; sx: SxProps<Theme> };
  ariaLabel?: string;
}

interface Props {
  columns: HeaderColumn[];
  sort: SortState | null;
  onSort: (key: string, numeric: boolean) => void;
}

const heights = (depth: number) =>
  Array.from({ length: depth }, (_, i) => (i === depth - 1 ? LEAF_ROW_HEIGHT : GROUP_ROW_HEIGHT));

export function GroupedHeader({ columns, sort, onSort }: Props) {
  const depth = Math.max(...columns.map((c) => c.path.length));
  const rowHeights = heights(depth);
  const top = (level: number) => rowHeights.slice(0, level).reduce((a, b) => a + b, 0);
  const rows: ReactNode[][] = Array.from({ length: depth }, () => []);

  for (let level = 0; level < depth; level++) {
    let i = 0;
    while (i < columns.length) {
      const col = columns[i];
      if (level >= col.path.length) {
        i++;
        continue;
      }
      if (level === col.path.length - 1) {
        rows[level].push(
          <LeafCell
            key={col.key}
            column={col}
            rowSpan={depth - level}
            top={top(level)}
            sort={sort}
            onSort={onSort}
          />,
        );
        i++;
        continue;
      }
      const prefix = col.path.slice(0, level + 1).join('\u0000');
      let span = 1;
      while (
        i + span < columns.length &&
        columns[i + span].path.length > level + 1 &&
        columns[i + span].path.slice(0, level + 1).join('\u0000') === prefix
      ) {
        span++;
      }
      const hint = col.pathHints?.[level];
      rows[level].push(
        <TableCell
          key={`${level}:${col.key}`}
          colSpan={span}
          align="center"
          sx={{ ...headCellSx, top: top(level), borderLeft: 1, borderLeftColor: 'divider' }}
        >
          {col.path[level]}
          {hint && <HeaderHint text={hint} />}
        </TableCell>,
      );
      i += span;
    }
  }

  return (
    <>
      {rows.map((cells, level) => (
        <TableRow key={level} sx={{ height: rowHeights[level] }}>
          {cells}
        </TableRow>
      ))}
    </>
  );
}

/** 見出しに添える説明。「i」のアイコンにマウスを乗せるか、キーボードで選ぶと吹き出しで出す */
function HeaderHint({ text }: { text: string }) {
  return (
    <Tooltip title={text} arrow>
      <InfoOutlinedIcon
        tabIndex={0}
        aria-label={text}
        color="action"
        sx={{ fontSize: 16, ml: 0.5, verticalAlign: 'text-bottom', cursor: 'pointer' }}
      />
    </Tooltip>
  );
}

function LeafCell({
  column,
  rowSpan,
  top,
  sort,
  onSort,
}: {
  column: HeaderColumn;
  rowSpan: number;
  top: number;
  sort: SortState | null;
  onSort: (key: string, numeric: boolean) => void;
}) {
  const name = column.path[column.path.length - 1];
  const label = (
    <>
      <Box component="span" display="block">
        {name}
      </Box>
      {column.unit && (
        <Box component="span" display="block" fontWeight="normal">
          {column.unit}
        </Box>
      )}
    </>
  );
  const active = sort?.key === column.key;
  const cellSx = (column.cell?.sx ?? {}) as object;
  const body =
    column.content ??
    (column.sortable ? (
      <TableSortLabel
        active={active}
        direction={active ? sort.dir : column.numeric ? 'desc' : 'asc'}
        onClick={() => onSort(column.key, !!column.numeric)}
        sx={{ flexDirection: column.numeric ? 'row-reverse' : 'row', textAlign: 'inherit' }}
      >
        <Box component="span">{label}</Box>
      </TableSortLabel>
    ) : (
      label
    ));
  return (
    <TableCell
      rowSpan={rowSpan}
      className={column.cell?.className}
      aria-label={column.ariaLabel}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
      sx={{
        ...headCellSx,
        ...cellSx,
        top,
        textAlign: column.numeric ? 'right' : 'left',
      }}
    >
      {column.leading ? (
        <Box component="span" display="inline-flex" alignItems="center" gap={0.5}>
          {column.leading}
          {body}
        </Box>
      ) : (
        body
      )}
    </TableCell>
  );
}
